/**
 * screens/hooks/useQueueStream.ts
 * Live push via Server-Sent Events (SK house style — see sk-hospitality-adapter).
 * Opens ONE EventSource to sk-queue-api's /queues/:id/events and calls onChange
 * whenever the queue's state moves, so the caller refetches its read model. This
 * replaces per-4s polling: no event → no traffic.
 *
 * Resilience: SSE is best-effort. If the stream can't open or drops (proxy,
 * older browser, backend without the endpoint), the caller keeps its existing
 * interval poll as a fallback — the board never goes dead. `connected` reflects
 * whether the stream is live, so callers can slow the poll while it is.
 *
 * Auth: EventSource can't set headers; the FE uses httpOnly cookies, so
 * withCredentials sends them same-origin (the /queue-api dev proxy is
 * same-origin, matching prod topology).
 */

import { useEffect, useRef, useState } from 'react';
import { QUEUE_V1 } from '../../api/config';

export function useQueueStream(queueId: string | null, onChange: () => void): { connected: boolean } {
  const [connected, setConnected] = useState(false);
  // Keep the latest onChange without re-opening the stream every render.
  const cb = useRef(onChange);
  cb.current = onChange;

  useEffect(() => {
    if (!queueId || typeof EventSource === 'undefined') { setConnected(false); return; }
    let es: EventSource | null = null;
    let closed = false;
    try {
      es = new EventSource(`${QUEUE_V1}/queues/${queueId}/events`, { withCredentials: true });
      es.onopen = () => { if (!closed) setConnected(true); };
      es.addEventListener('change', () => { if (!closed) cb.current(); });
      es.onerror = () => {
        // Browser auto-reconnects; treat as "not live" so the poll fallback runs.
        if (!closed) setConnected(false);
      };
    } catch {
      setConnected(false);
    }
    return () => { closed = true; setConnected(false); es?.close(); };
  }, [queueId]);

  return { connected };
}
